import React from 'react';
import { Link } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { LegalPageLayout, Section, Callout, Bullets } from '@/components/LegalPageLayout';

const UPDATED = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

// Page <title>, canonical, Open Graph and Twitter tags for /about are set centrally in components/RouteSeo.jsx.
const About = () => (
  <LegalPageLayout
    title="About Option Wizard"
    lastUpdated={UPDATED}
    icon={Building2}
    testid="about-page"
    intro={
      <>
        <p className="text-sm uppercase tracking-widest text-amber-400/90 mb-2" data-testid="about-subtitle">Methodology and Disclosures</p>
        <p>
          Option Wizard is a subscription-based, <strong>educational</strong> options-analysis and
          decision-support platform. It combines deterministic, math-based analytics with AI-assisted explanations to help
          traders learn, compare and evaluate options strategies. It does not place trades, manage money or give
          personalized advice.
        </p>
      </>
    }
  >
    <Section title="What Option Wizard is">
      <Bullets items={[
        'Position Analyzer — examine an options position and evaluate how it may behave under different underlying stock prices and scenarios.',
        'Rollmaster — explore potential adjustments to an existing options position and compare alternative rolling strategies.',
        'Roll Comparison Calculator — compare alternative roll scenarios and understand their potential financial differences.',
        'Daily Pick — the free entry feature: a daily, AI-assisted list of stocks with option-strategy ideas and educational context.',
        'Options calculations and risk analysis — theoretical valuation, Greeks, breakevens, maximum profit/loss and profit-and-loss scenario analysis.',
        'AI-assisted explanations layered on top of the deterministic calculations; the AI narrates, it does not perform the options math.',
      ]} />
    </Section>

    <Section title="How the calculations work">
      <p>
        Every number shown in the analysis tools comes from a deterministic calculation engine. The same inputs always
        produce the same outputs, and the formulas below are standard, published models.
      </p>
      <Bullets items={[
        <><strong>Theoretical valuation (Black-Scholes).</strong> Theoretical option values are computed with the Black-Scholes model from the underlying price, strike, time to expiration, an implied-volatility input, a risk-free interest rate and a dividend-yield assumption. A theoretical value is a model output, not a tradable quote.</>,
        <><strong>Greeks.</strong> Delta (sensitivity to the underlying price), gamma (rate of change of delta), theta (time decay per day) and vega (sensitivity to a one-point change in implied volatility) are derived analytically from the same model and inputs.</>,
        <><strong>Breakevens, maximum profit and maximum loss.</strong> Computed from the position's legs — strikes, quantities, premiums paid or received and expiration — using the payoff at expiration. Where a position's loss is theoretically unlimited (for example an uncovered short call) it is reported as unlimited, never as a finite number.</>,
        <><strong>Scenario analysis.</strong> Profit-and-loss scenarios re-price the position across a range of underlying prices and, where applicable, dates and volatility assumptions, using the same valuation model. Scenarios are illustrations of how a model responds to inputs, not forecasts.</>,
        <><strong>Rollmaster and quotes.</strong> Rollmaster evaluates candidate rolls from actual option-chain bid/ask quotes: legs being sold are valued at the <em>bid</em> and legs being bought at the <em>ask</em>, so net credits and debits reflect the less favourable side of the spread. Candidates with missing or invalid bid/ask data are excluded and reported as insufficient data rather than estimated.</>,
      ]} />
      <p className="text-sm text-slate-400">
        Model assumptions — volatility, interest rate, dividend yield and valuation date — are shown alongside results where they
        apply. Changing an assumption changes the result; the tools do not know the "right" assumption for your situation.
      </p>
    </Section>

    <Section title="Data limitations">
      <Bullets items={[
        <><strong>Delayed market data.</strong> Stock prices, option chains and quotes come from third-party data providers and may be delayed, incomplete or temporarily unavailable. They are not real-time exchange feeds and are not suitable for order placement. Always confirm prices with your broker.</>,
        <><strong>Demo uses synthetic data.</strong> The public interactive demonstration runs the real calculation engine on clearly labelled synthetic inputs. Demo mode is not live market quotes and is not executable.</>,
        <><strong>Daily Pick is educational context, not a signal.</strong> Each day an AI model produces a list of stocks with option-strategy ideas from the data it is given. These are AI-generated educational ideas, not trade signals, recommendations or guarantees. Past picks are published transparently in the Pick History together with the stock-price changes that were actually observed afterwards; those figures are stock-price movement only and are <em>not</em> the profit or loss of any options strategy.</>,
        <><strong>AI output can be wrong.</strong> AI-generated explanations may contain errors, omissions or unsuitable suggestions and should be treated as one input among many.</>,
      ]} />
    </Section>

    <Section title="Risk disclosure">
      <Callout tone="red" title="Options trading involves substantial risk of loss and is not suitable for everyone.">
        You can lose more than your initial investment. Analytical outputs are models and illustrations, not guarantees of
        future results. Market prices and option quotes can change at any time. Option Wizard provides educational tools
        and information only; it does not provide financial, investment or trading advice, does not make personalized
        recommendations and does not guarantee any outcome. Consult a qualified, licensed financial professional before
        making investment decisions. See the{' '}
        <Link to="/safety" className="underline underline-offset-4">Safety Policy</Link>,{' '}
        <Link to="/terms" className="underline underline-offset-4">Terms of Service</Link> and the{' '}
        <Link to="/legal" className="underline underline-offset-4">Legal Center</Link>.
      </Callout>
    </Section>

    <Section title="Company information">
      <p>&ldquo;Option Wizard&rdquo; is the product name. The service is operated by:</p>
      <address className="not-italic rounded-lg border border-slate-700 bg-slate-800/50 p-4 text-slate-200 leading-relaxed" data-testid="about-business-address">
        Timothy Woods<br />Option Wizard<br />200 N. Vineyard Blvd<br />Ste A325-186<br />Honolulu, Hawaii 96817
      </address>
      <p className="text-sm text-slate-400">
        &ldquo;Option Wizard&rdquo; is the operating business name. No corporate designation (such as LLC or Inc.) is
        claimed, and no registered-entity status is asserted pending verification.
      </p>
    </Section>

    <Section title="Contact">
      {/* TODO(owner): no public support e-mail address exists anywhere on the site; add one here (and in Contact.jsx) once confirmed. */}
      <p>
        Questions, feedback or support requests? Reach us through the{' '}
        <Link to="/contact" className="text-amber-300 hover:underline underline-offset-4" data-testid="about-contact-link">contact form</Link>.
        Postal correspondence can be sent to the address above.
      </p>
    </Section>
  </LegalPageLayout>
);

export default About;
